import React, { FC, useCallback, useMemo, useState } from 'react'
import classNames from 'classnames'
import { InView } from 'react-intersection-observer'
import { Button } from '@/atoms/Button'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { CircleCheckIcon } from '@/atoms/Icons/CircleCheckIcon'
import { CircleCloseIcon } from '@/atoms/Icons/CircleCloseIcon'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { slugs } from '@/constants'
import { formatPrice } from '@/services/PriceFormatter'
import { ShopifyProductWithBundles } from '@/services/Shopify/ShopifyService'
import { useShopifyCart } from '@/services/Shopify/hooks'
import { MoneyCurrencyCode } from '@/types/codegen-federation'
import { mergeUrlParams } from '@/utils/UriBuilder/UriBuilder'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { GiveawaySupportModal } from '@/views/PreSales/PreSalesGiveawayView/components/GiveawaySupportModal'
import { useGiveawayProductController } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawayProductController'
import { useGiveawaySchedule } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawaySchedule'
import { useShopifyUserAttributes } from '@/views/PreSales/hooks/useShopifyUserAttributes'
import useHandleUnexpectedError from '@/views/TicketCheckoutViews/hooks/useHandleUnexpectedError'
import {
  GButtonMD,
  GCaptionSM,
  GEyebrowMD,
  GEyebrowSM,
  GFooterMD,
  GParagraphSM,
  GTitleSM,
  GTitleXL,
} from './Typography'

interface Props {
  tickets: ShopifyProductWithBundles[]
  theatricalSlug: string
  entriesProductId: string
}

export const PresaleChipSelectorCard: FC<Props> = ({ tickets, theatricalSlug, entriesProductId }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false)
  const { t } = useTranslate('tickets')
  const { trackProductAdded, trackProductViewed } = useEcommerceEvents()
  const { push } = useSafeRouter()
  const { handleUnexpectedError } = useHandleUnexpectedError({ reservationId: 'unknown' })
  const { createCart } = useShopifyCart()
  const { normal, isActive, minRewardTicketQuantity, dailyBonusEntries } = useGiveawaySchedule(theatricalSlug)
  const multiplier = normal?.multiplier ?? 1
  const formattedSlug = theatricalSlug === 'rule-breakers-tech' ? slugs.ruleBreakers : theatricalSlug

  const {
    selectedTicketsQty,
    selectedProduct,
    ticketQuantityList,
    totalSelectedEntries,
    handlePerkClick,
    handleProductChange,
    allPerks,
    checkIsPerkActive,
    activePerkIds,
  } = useGiveawayProductController(tickets, theatricalSlug)

  const attributes = useShopifyUserAttributes('theatrical-giveaway', 'theatrical giveaway ticket', theatricalSlug)

  const handleCheckout = useCallback(
    async (product: ShopifyProductWithBundles) => {
      const lines: Array<{ quantity: number; merchandiseId: string; sellingPlanId?: string }> = [
        { quantity: 1, merchandiseId: product?.variants?.edges[0]?.node?.id },
        {
          quantity: totalSelectedEntries,
          merchandiseId: `gid://shopify/ProductVariant/${entriesProductId}`,
        },
      ]

      try {
        setIsLoading(true)

        const { cart, errors } = await createCart({
          lines,
          attributes,
        })

        if (errors && errors.length > 0) {
          setIsSupportModalOpen(true)
          return
        }

        const checkoutUrl = mergeUrlParams(cart?.checkoutUrl, { utm_term: 'from-angel-com-flow' })

        if (checkoutUrl) {
          await trackProductAdded({
            category: 'theatrical pre-order ticket',
            funnel: 'theatrical-pre-sale',
            sku: `pre-order-${theatricalSlug}`,
            product_id: `pre-order-${theatricalSlug}`,
            name: `${theatricalSlug} theatrical pre-order ticket`,
            price: parseInt(product?.priceRange?.maxVariantPrice?.amount ?? 0),
            currency: (product?.priceRange?.maxVariantPrice?.currencyCode as MoneyCurrencyCode) ?? 'USD',
            quantity: 1,
            projectSlug: theatricalSlug,
          })
          return push(checkoutUrl)
        }
      } catch (error) {
        handleUnexpectedError({
          context: 'AddPreOrderTicketOption',
          error,
          customToastMessage: t('somethingWentWrongPleaseTryAgainPreOrder', 'Something went wrong, please try again.'),
        })
      } finally {
        setIsLoading(false)
      }
    },
    [
      attributes,
      createCart,
      entriesProductId,
      handleUnexpectedError,
      push,
      t,
      theatricalSlug,
      totalSelectedEntries,
      trackProductAdded,
    ],
  )

  const isLowerPricedTickets = useMemo(() => {
    return selectedTicketsQty >= 30
  }, [selectedTicketsQty])

  function handleInitialCheckoutIntent(): void {
    selectedProduct && handleCheckout(selectedProduct)
  }

  return (
    <InView
      triggerOnce
      onChange={(inView) => {
        if (!inView) return

        trackProductViewed({
          category: 'theatrical pre-order ticket',
          funnel: 'theatrical-pre-sale',
          sku: `pre-order-${theatricalSlug}`,
          product_id: `pre-order-${theatricalSlug}`,
          name: `${theatricalSlug} theatrical pre-order ticket`,
          price: Number((selectedProduct?.priceRange?.maxVariantPrice?.amount ?? 0) * 100),
          currency: 'USD',
          quantity: 1,
          projectSlug: theatricalSlug,
        })
      }}
    >
      <div className="flex flex-col">
        <div className="mx-auto flex w-full flex-col gap-6 rounded-2xl border border-[#E5E8EF] bg-[#F5F5F5] px-8 py-6 md:rounded-3xl md:bg-white">
          <div className="flex flex-col items-center gap-1">
            <GEyebrowSM
              color="white"
              className={classNames(
                'mb-1 rounded-md bg-giveaway-primary-accent px-2 py-1 uppercase',
                isLowerPricedTickets ? 'visible' : 'invisible',
              )}
            >
              {t('tenPerTicket', '$10 per ticket')}
            </GEyebrowSM>

            <FlexRow className="!items-top">
              <GTitleSM color="giveaway-text-primary-light" className="mb-auto block">
                $
              </GTitleSM>
              <GTitleXL color="giveaway-text-primary-light" className="items-top flex">
                {formatPrice((selectedProduct?.priceRange?.maxVariantPrice?.amount ?? 0) * 100, {
                  currency: selectedProduct?.priceRange?.maxVariantPrice?.currencyCode ?? 'USD',
                  ignoreEmptyDecimal: true,
                  style: 'decimal',
                })}
              </GTitleXL>
            </FlexRow>
            <FlexColumn className="text-center">
              <GTitleSM color="giveaway-text-subtle-light">
                {t('fullPriceTicketsSelectedQty', '{{selectedQty}} Tickets', { selectedQty: selectedTicketsQty })}
              </GTitleSM>
            </FlexColumn>
            {isActive && multiplier > 0 && (
              <FlexColumn className="mt-2">
                <div className="flex flex-col items-center">
                  <GEyebrowSM
                    className="rounded-md bg-giveaway-secondary px-2 pb-[5px] pt-1"
                    color="giveaway-text-primary-light"
                  >
                    {totalSelectedEntries} {t('giveawayEntriesV2', 'GIVEAWAY ENTRIES')}
                  </GEyebrowSM>

                  {dailyBonusEntries > 0 && selectedTicketsQty >= (minRewardTicketQuantity ?? 0) && (
                    <GCaptionSM color="giveaway-text-primary-light" className="mt-2" italic>
                      <Translate i18nKey="dailyBonusEntries" values={{ dailyBonusEntries }} t={t}>
                        Includes{' '}
                        <span className="font-bold text-giveaway-secondary-accent">
                          {{ dailyBonusEntries }} bonus entries
                        </span>{' '}
                        today only!
                      </Translate>
                    </GCaptionSM>
                  )}

                  {!dailyBonusEntries && (
                    <GCaptionSM color="giveaway-text-primary-light" className="mt-2" italic>
                      <Translate i18nKey="bonusEntriesCardGiveawayV3" values={{ multiplier }} t={t}>
                        Includes{' '}
                        <span className="font-bold text-giveaway-secondary-accent">
                          {{ multiplier }}x bonus entries
                        </span>{' '}
                        this week only!
                      </Translate>
                    </GCaptionSM>
                  )}
                </div>
              </FlexColumn>
            )}
          </div>

          <div className="flex flex-col gap-4">
            <GEyebrowMD color="giveaway-text-primary-light" className="uppercase">
              {t('ticketQuantityPreSales', 'Ticket Quantity')}:
            </GEyebrowMD>

            <ul className="flex grow-1 flex-wrap justify-center gap-3">
              {ticketQuantityList.map((ticket) => {
                const isSelectedOffer = ticket?.id === selectedProduct?.id
                return (
                  <li className="!w-full min-w-[64px] max-w-[64px] md:!max-w-[70px]" key={ticket.id}>
                    <Button
                      onClick={() => handleProductChange(ticket.id)}
                      className={classNames(
                        'rounded-full !text-sm !border-none h-[40px] grow-1 !w-full relative',
                        isSelectedOffer ? '!bg-core-gray-950 !text-white' : '!bg-[#D3D3D6B2]/70',
                      )}
                      variant="lightgray"
                    >
                      {ticket.quantity}
                    </Button>
                  </li>
                )
              })}
            </ul>
          </div>

          {theatricalSlug === slugs.homestead && (
            <div className="flex flex-col gap-4">
              <GEyebrowMD className="uppercase" color="giveaway-primary">
                {t('superFanBenefits', 'Superfan Benefits')}: ({activePerkIds?.size + 2} / {allPerks?.length})
              </GEyebrowMD>
              <ul className="mx-2 flex flex-col gap-2">
                {allPerks?.map((perk) => {
                  const isActive = checkIsPerkActive(perk?.id ?? '')
                  return (
                    <li
                      onClick={() => handlePerkClick(perk?.id as string)}
                      key={perk?.id}
                      className={classNames('flex items-start gap-2 cursor-pointer', !isActive && 'opacity-40')}
                    >
                      <div className="shrink-0 pt-[4px]">
                        {isActive ? (
                          <CircleCheckIcon className="shrink-0" size={14} color2="white" color1="giveaway-primary" />
                        ) : (
                          <CircleCloseIcon className="shrink-0" color="giveaway-subtle" size={14} />
                        )}
                      </div>

                      <GParagraphSM
                        className={classNames(
                          isActive ? 'text-giveaway-text-primary-light' : 'text-giveaway-text-subtle-light',
                        )}
                      >
                        {perk?.title}
                      </GParagraphSM>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {selectedProduct && (
            <button
              disabled={isLoading}
              onClick={() => handleInitialCheckoutIntent()}
              className={classNames('rounded-[14px] !bg-giveaway-secondary-accent px-5 py-[0.906rem]')}
            >
              <GButtonMD color="white" className="flex w-full items-center justify-between">
                <span className="flex items-center gap-2">
                  {t('Next', 'Next')}
                  {isLoading && <LoadingSpinnerIcon size={18} color="white" />}
                </span>
                <span className="gap flex items-center text-white/80">
                  {formatPrice((selectedProduct?.priceRange?.maxVariantPrice?.amount ?? 0) * 100, {
                    currency: selectedProduct?.priceRange?.maxVariantPrice?.currencyCode ?? 'USD',
                    ignoreEmptyDecimal: true,
                  })}
                  <CaretRightIcon color="white" className="opacity-80" size={28} />
                </span>
              </GButtonMD>
            </button>
          )}
        </div>
        <GFooterMD color="giveaway-text-subtle-light" className="mx-auto mt-6 max-w-[420px] px-4">
          <Translate t={t} i18nKey="presaleChipDisclaimerV4" values={{ formattedSlug }}>
            Your ticket codes will be emailed after purchase. Redeem them at{' '}
            <a href={`https://angel.com/tickets/${formattedSlug}`} target="_blank" className="underline">
              angel.com/tickets/{{ formattedSlug }}
            </a>{' '}
            when showtimes are available. Questions? Check our FAQs!
          </Translate>
        </GFooterMD>
      </div>

      <GiveawaySupportModal
        open={isSupportModalOpen}
        onClose={() => {
          setIsSupportModalOpen(false)
        }}
      />
    </InView>
  )
}
